/**
 * Rich text for Markdown fields (TipTap). The stored value stays Markdown. The text is only re-serialised once the
 * editor actually types in it, so opening and saving a page never reformats its Markdown. A "Markdown" toggle shows
 * the source for anything the toolbar can't do.
 */
import { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor as useTiptap, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Markdown } from '@tiptap/markdown';
import { useT } from '../i18n.js';
import { useToast } from '../state.js';
import { useEditor } from './context.js';
import { ImageError } from '../lib/images.js';

export function RichText({ value, onChange, label, invalid }: { value: string; onChange: (v: string) => void; label: string; invalid: boolean }) {
  const { t } = useT();
  const ctx = useEditor();
  const toast = useToast();
  const [source, setSource] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const resolver = useRef(ctx.imageSrc);
  resolver.current = ctx.imageSrc;
  const lastEmitted = useRef(value);

  const editor = useTiptap({
    extensions: [
      StarterKit.configure({ underline: false, link: { openOnClick: false, autolink: true } }),
      // Images keep their Markdown path (e.g. ./images/x.webp) in the document; only the displayed src is resolved.
      Image.extend({
        addNodeView() {
          return ({ node }) => {
            const img = document.createElement('img');
            img.src = resolver.current(node.attrs.src) ?? node.attrs.src;
            img.alt = node.attrs.alt ?? '';
            return {
              dom: img,
              update: (n) => {
                if (n.type.name !== 'image') return false;
                img.src = resolver.current(n.attrs.src) ?? n.attrs.src;
                img.alt = n.attrs.alt ?? '';
                return true;
              },
            };
          };
        },
      }).configure({ inline: false }),
      Markdown,
    ],
    content: value,
    contentType: 'markdown',
    editorProps: { attributes: { 'aria-label': label, role: 'textbox', 'aria-multiline': 'true', ...(invalid ? { 'aria-invalid': 'true' } : {}) } },
    onUpdate: ({ editor: ed }) => {
      const md = ed.getMarkdown();
      lastEmitted.current = md;
      onChange(md);
    },
  });

  // Outside changes (restore, translation switch, source mode): load them into the editor.
  useEffect(() => {
    if (!editor || value === lastEmitted.current) return;
    lastEmitted.current = value;
    editor.commands.setContent(value, { emitUpdate: false, contentType: 'markdown' });
  }, [value, editor]);

  const insertImage = async (files: File[]) => {
    if (!editor || !files.length) return;
    try {
      const values = await ctx.upload(files, {});
      for (const src of values) editor.chain().focus().setImage({ src, alt: '' }).run();
    } catch (e) {
      toast(e instanceof ImageError ? t('notAnImage', { name: e.fileName }) : (e as Error).message);
    }
  };

  return (
    <div className="rte">
      <div className="bar" role="toolbar" aria-label={label}>
        {!source && editor && <Toolbar editor={editor} onImage={() => file.current?.click()} />}
        <span className="right" />
        <button type="button" className={source ? 'on' : ''} aria-pressed={source} onClick={() => setSource((s) => !s)}>
          {t('markdownSource')}
        </button>
      </div>
      {source ? (
        <textarea
          className="source"
          value={value}
          aria-label={`${label} (Markdown)`}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ) : (
        <div className="content">
          <EditorContent editor={editor} />
        </div>
      )}
      <input
        ref={file}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={async (e) => {
          await insertImage([...(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function Toolbar({ editor, onImage }: { editor: Editor; onImage: () => void }) {
  const { t } = useT();
  const [, force] = useState(0);
  useEffect(() => {
    const update = () => force((n) => n + 1);
    editor.on('transaction', update);
    return () => {
      editor.off('transaction', update);
    };
  }, [editor]);
  const btn = (label: string, title: string, active: boolean, run: () => void) => (
    <button type="button" className={active ? 'on' : ''} aria-pressed={active} title={title} aria-label={title} onMouseDown={(e) => e.preventDefault()} onClick={run}>
      {label}
    </button>
  );
  const c = () => editor.chain().focus();
  return (
    <>
      {btn('B', 'Bold', editor.isActive('bold'), () => c().toggleBold().run())}
      {btn('I', 'Italic', editor.isActive('italic'), () => c().toggleItalic().run())}
      <span className="sep" />
      {btn('H2', 'Heading 2', editor.isActive('heading', { level: 2 }), () => c().toggleHeading({ level: 2 }).run())}
      {btn('H3', 'Heading 3', editor.isActive('heading', { level: 3 }), () => c().toggleHeading({ level: 3 }).run())}
      <span className="sep" />
      {btn('•', 'Bullet list', editor.isActive('bulletList'), () => c().toggleBulletList().run())}
      {btn('1.', 'Numbered list', editor.isActive('orderedList'), () => c().toggleOrderedList().run())}
      {btn('“', 'Quote', editor.isActive('blockquote'), () => c().toggleBlockquote().run())}
      <span className="sep" />
      {btn('Link', 'Link', editor.isActive('link'), () => {
        const prev = editor.getAttributes('link').href as string | undefined;
        const href = window.prompt(t('linkPrompt'), prev ?? 'https://');
        if (href === null) return;
        if (!href || href === 'https://') c().unsetLink().run();
        else c().extendMarkRange('link').setLink({ href }).run();
      })}
      {btn('Img', t('upload'), false, onImage)}
      <span className="sep" />
      {btn('↶', 'Undo', false, () => c().undo().run())}
      {btn('↷', 'Redo', false, () => c().redo().run())}
    </>
  );
}
